import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import Button from 'components/UI/Buttons/BaseButton';
import { MessageType } from 'components/UI/snackbar/providers/SnackBarProvider';

import { useAppDispatch } from 'src/app/hooks';
import ProformaInvoiceEntity from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import {
  dismissProformaInvoiceReview,
  updateProformaInvoiceFromQuote,
} from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';
import styles from './InvoiceDrawer.module.css';

interface Props {
  invoice: ProformaInvoiceEntity;
  // Called after either action succeeds; the drawer closes so the form never
  // shows the content from before "Update from quote".
  onResolved?: () => void;
}

/**
 * Server review mark (LAE-752): the customer signed the linked quote and the
 * signed quote differs from this invoice. Nothing changes until the user picks
 * an action.
 */
export default function InvoiceReviewBanner({ invoice, onResolved }: Props) {
  const { t } = useTranslation('proforma-invoices-page');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState<'update' | 'dismiss' | null>(null);

  if (!invoice.reviewRequiredAt) {
    return null;
  }

  const run = async (action: 'update' | 'dismiss') => {
    setPending(action);
    try {
      const thunk =
        action === 'update' ? updateProformaInvoiceFromQuote : dismissProformaInvoiceReview;
      await dispatch(thunk(invoice.uuid)).unwrap();
      enqueueSnackbar({
        message: t(
          action === 'update' ? 'review.notification.updated' : 'review.notification.dismissed'
        ),
        variant: MessageType.Success,
      });
      onResolved?.();
    } catch (error) {
      enqueueSnackbar({ message: t('common:message.server_error'), variant: MessageType.Error });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className={styles.reviewBanner} role="status">
      <div className={styles.reviewTitle}>{t('review.title')}</div>
      <div className={styles.reviewText}>
        {t('review.description', { date: new Date(invoice.reviewRequiredAt).toLocaleDateString() })}
      </div>
      <div className={styles.reviewActions}>
        {invoice.priceProposalId && (
          <Button
            textSize="small"
            onClick={() => run('update')}
            disabled={pending !== null}
            isProcessing={pending === 'update'}
          >
            {t('review.actions.update')}
          </Button>
        )}
        <Button
          variant="outlined"
          textSize="small"
          onClick={() => run('dismiss')}
          disabled={pending !== null}
          isProcessing={pending === 'dismiss'}
        >
          {t('review.actions.dismiss')}
        </Button>
      </div>
    </div>
  );
}
